import { useState, useEffect } from "react";
import { attPath } from "@/lib/attHost";
import { useNavigate } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function AttendanceLogin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useSEO({ title: "Attendance Login — Mashara Finance", description: "Center login for biometric attendance and fingerprint enrollment.", path: "/attendance", noindex: true });

  useEffect(() => { if (user && ["admin", "center_operator"].includes(user.role)) navigate(attPath("/app"), { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      if (!["admin", "center_operator"].includes(u.role)) { await logout(); setError("This account does not have attendance access. Ask your admin to create a center login."); return; }
      navigate(attPath("/app"));
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="attendance-login-page" className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-11 h-11 rounded-xl bg-emerald-700 text-white flex items-center justify-center"><Fingerprint className="w-6 h-6" /></span>
            <div><h1 className="font-display text-2xl font-bold text-slate-900">Attendance Login</h1><p className="text-sm text-slate-600">Biometric fingerprint enrollment & verification — Mantra L1</p></div>
          </div>
          <form onSubmit={submit} className="mt-4 space-y-4" data-testid="attendance-login-form">
            <div className="space-y-1.5"><Label htmlFor="att-email">Email (login ID)</Label><Input id="att-email" data-testid="attendance-login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="center@company.com" /></div>
            <div className="space-y-1.5"><Label htmlFor="att-password">Password</Label><Input id="att-password" data-testid="attendance-login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            {error && <p data-testid="attendance-login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="attendance-login-submit" disabled={loading} className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-6 font-semibold">{loading ? "Signing in…" : "Sign In"}</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
